import { FC, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useAdminAddTranscriptMutation, useGetCourseCategoriesQuery } from '@teachur/http-client';

import Modal from '@new/common/ui/Modal/Modal';
import { Button } from '@new/common/ui/Button/Button';
import { Input } from '@new/common/ui/Input/Input';
import { ButtonInputs, ButtonInputsOption } from '@new/common/ui/ButtonInputs/ButtonInputs';
import { SectionHeading } from '@new/common/ui/SectionHeading/SectionHeading';
import {
  StudentSearchSelect,
  type SelectedStudent,
} from '@new/common/ui/StudentSearchSelect/StudentSearchSelect';

import {
  CourseRowsEditor,
  type EditableCourse,
} from '@new/features/transcripts/shared/components/CourseRowsEditor';
import { makeKeyGen, toCoursePayload } from '@new/features/transcripts/student/hooks/reviewState';

const YES_NO_OPTIONS: ButtonInputsOption<'yes' | 'no'>[] = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

const nextKey = makeKeyGen('ac');

const emptyCourse = (): EditableCourse => ({
  key: nextKey(),
  rawName: '',
  rawCode: null,
  creditHours: null,
  grade: null,
  categoryIds: [],
});

export interface AdminAddTranscriptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdded?: () => void;
  lockedStudent?: SelectedStudent;
}

export const AdminAddTranscriptModal: FC<AdminAddTranscriptModalProps> = ({
  isOpen,
  onClose,
  onAdded,
  lockedStudent,
}) => {
  const [student, setStudent] = useState<SelectedStudent | null>(lockedStudent ?? null);
  const [institution, setInstitution] = useState('');
  const [institutionUrl, setInstitutionUrl] = useState('');
  const [officialTranscriptRequested, setOfficialTranscriptRequested] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);
  const [courses, setCourses] = useState<EditableCourse[]>([]);

  const addTranscript = useAdminAddTranscriptMutation();
  const categoriesQuery = useGetCourseCategoriesQuery();
  const categories = categoriesQuery.data ?? [];

  const reset = () => {
    setStudent(lockedStudent ?? null);
    setInstitution('');
    setInstitutionUrl('');
    setOfficialTranscriptRequested(false);
    setCredits(null);
    setCourses([]);
  };

  const close = () => {
    reset();
    onClose();
  };

  const updateCourse = (key: string, patch: Partial<EditableCourse>) =>
    setCourses((rows) => rows.map((c) => (c.key === key ? { ...c, ...patch } : c)));

  const canSave = useMemo(
    () =>
      student != null &&
      institution.trim().length > 0 &&
      credits !== null &&
      Number(credits) >= 0 &&
      !addTranscript.isPending,
    [student, institution, credits, addTranscript.isPending]
  );

  const handleSave = async () => {
    if (student == null || !canSave) return;

    await addTranscript.mutateAsync(
      {
        userId: student.id,
        body: {
          institution: institution.trim(),
          institutionUrl: institutionUrl.trim() || null,
          accreditingOrganizations: null,
          awardedDegree: null,
          officialTranscriptRequested,
          amount: Number(credits) || 0,
          isOfficial: false,
          courses: toCoursePayload(courses),
        },
      },
      {
        onSuccess: () => {
          toast.success('Transcript added');
          onAdded?.();
          close();
        },
        onError: () => toast.error('Failed to add transcript'),
      }
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      dismissable={false}
      title="Create transcript for a student"
      contentClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
      className="max-w-5xl! md:max-w-5xl! lg:max-w-5xl! [&_input[data-slot=input]]:h-[48px]! [&_input[data-slot=input]]:md:h-[48px]! [&_input[data-slot=input]]:xl:h-[48px]! [&_input[data-slot=input]]:rounded-[8px]! [&_input[data-slot=input]]:border-[1.5px]! [&_input[data-slot=input]]:border-solid! [&_input[data-slot=input]]:border-[#E1E7EA]! [&_input[data-slot=input]]:bg-[#FCFDFD]!"
    >
      <div className="flex h-full flex-col gap-6 overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pr-1">
          <fieldset className="m-0! flex flex-col gap-4 border-0 p-0">
            <SectionHeading title="Student" />
            <div className="flex flex-col gap-2">
              <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
                Select a student
              </label>
              <StudentSearchSelect
                value={student}
                onChange={setStudent}
                disabled={!!lockedStudent}
              />
            </div>
          </fieldset>

          <fieldset className="m-0! flex flex-col gap-4 border-0 p-0">
            <SectionHeading title="Basic information" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
                  Institution
                </label>
                <Input
                  value={institution}
                  placeholder="e.g. Arizona State University"
                  onChange={(v) => setInstitution(v)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
                  Requested credits
                </label>
                <Input<number | string>
                  type="number"
                  min={0}
                  step={1}
                  value={credits ?? ''}
                  onChange={(v) => setCredits(v === '' ? null : Math.round(Number(v)))}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
                  Institution website (optional)
                </label>
                <Input
                  value={institutionUrl}
                  placeholder="e.g. https://asu.edu"
                  onChange={(v) => setInstitutionUrl(v)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
                  Official transcript requested
                </label>
                <ButtonInputs<'yes' | 'no'>
                  size="input"
                  value={officialTranscriptRequested ? 'yes' : 'no'}
                  onChange={(v) => setOfficialTranscriptRequested(v === 'yes')}
                  options={YES_NO_OPTIONS}
                />
              </div>
            </div>
          </fieldset>

          <CourseRowsEditor
            courses={courses}
            categories={categories}
            onAdd={() => setCourses((rows) => [...rows, emptyCourse()])}
            onUpdate={updateCourse}
            onRemove={(key) => setCourses((rows) => rows.filter((c) => c.key !== key))}
            helpText="It's the student's responsibility to add every course and verify that the names, codes, and credits are correct before submitting."
          />
        </div>

        <div className="flex shrink-0 items-center justify-between border-t border-(--neutral-black-NB100) pt-4">
          <Button variant="neutral" onClick={close} text="Cancel" />
          <Button
            variant="primary"
            disabled={!canSave}
            onClick={handleSave}
            text={addTranscript.isPending ? 'Saving…' : 'Save'}
          />
        </div>
      </div>
    </Modal>
  );
};
